import "./LocationsCard.css";

interface DealerLocation {
  name: string;
  rating: number;
  reviewCount: number;
  category: string;
  statusText: string;
  address: string;
  description?: string;
  phone: string;
  websiteUrl: string;
  imageUrl?: string;
  mapsQuery: string;
}

const LAMBORGHINI_SEATTLE_LOCATIONS: DealerLocation[] = [
  {
    name: "GQ Motorsports",
    rating: 4.9,
    reviewCount: 483,
    category: "Used car dealer",
    statusText: "Closed · Opens 10 AM Mon",
    address: "34622 W Valley Hwy S, Algona, WA 98001",
    description:
      "Family-owned dealership with quality used vehicles, financing, and five-star service.",
    phone: "+12539480101",
    websiteUrl: "https://www.gqmotorsportsllc.com/",
    imageUrl: "https://www.gqmotorsportsllc.com/favicon.ico",
    mapsQuery: "GQ Motorsports Algona WA",
  },
  {
    name: "Ferrari of Seattle Showroom",
    rating: 4.5,
    reviewCount: 41,
    category: "Ferrari dealer",
    statusText: "Closed · Opens 9 AM Mon",
    address: "13120 NE Bel Red Rd, Bellevue, WA 98005",
    description: "Official Ferrari showroom serving the Seattle and Pacific Northwest area.",
    phone: "+12063297070",
    websiteUrl: "https://www.ferrariofseattle.com/",
    imageUrl: "https://www.ferrariofseattle.com/favicon.ico",
    mapsQuery: "Ferrari of Seattle Showroom Bellevue WA",
  },
  {
    name: "Ferrari of Seattle Service",
    rating: 4.6,
    reviewCount: 10,
    category: "Car repair and maintenance service",
    statusText: "Closed · Opens 7:30 AM Mon",
    address: "7410 185th Ave NE, Redmond, WA 98052",
    description: "Authorized Ferrari service workshop with factory-trained technicians.",
    phone: "+12063297070",
    websiteUrl: "https://www.ferrariofseattle.com/service.aspx",
    imageUrl: "https://www.ferrariofseattle.com/favicon.ico",
    mapsQuery: "Ferrari of Seattle Service Redmond WA",
  },
  {
    name: "Maserati of Seattle",
    rating: 4.4,
    reviewCount: 7,
    category: "Maserati dealer",
    statusText: "Closed · Opens 9 AM Mon",
    address: "1401 12th Ave, Seattle, WA 98122",
    description: "Seattle Maserati dealership for new and pre-owned vehicles, service, and parts.",
    phone: "+12069570557",
    websiteUrl: "https://www.maseratiofseattle.com/",
    imageUrl: "https://www.maseratiofseattle.com/favicon.ico",
    mapsQuery: "Maserati of Seattle Seattle WA",
  },
  {
    name: "Ferrari of Seattle Parts",
    rating: 5.0,
    reviewCount: 1,
    category: "Auto parts store",
    statusText: "Closed · Opens 8 AM Mon",
    address: "7410 185th Ave NE, Redmond, WA 98052",
    description: "Ferrari Genuine parts and accessories at the Redmond service center.",
    phone: "+12063297070",
    websiteUrl: "https://www.ferrariofseattle.com/parts.html",
    imageUrl: "https://www.ferrariofseattle.com/favicon.ico",
    mapsQuery: "Ferrari of Seattle Parts Redmond WA",
  },
];

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function mapsSearchUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function directionsUrl(location: DealerLocation): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location.address)}`;
}

function renderStars(rating: number): string {
  const rounded = Math.max(0, Math.min(5, Math.round(rating)));
  return `<span class="ayesearch-location-stars" aria-hidden="true">${"★".repeat(rounded)}${"☆".repeat(5 - rounded)}</span>`;
}

function renderLocation(location: DealerLocation, index: number): string {
  const hiddenClass = index >= 3 ? " ayesearch-location-collapsed" : "";
  const image = location.imageUrl
    ? `<img class="ayesearch-location-image" src="${escapeHtml(location.imageUrl)}" alt="" loading="lazy" referrerpolicy="no-referrer" />`
    : "";

  return `
    <article class="ayesearch-location-item${hiddenClass}" data-location-index="${index}">
      <div class="ayesearch-location-main">
        <div class="ayesearch-location-copy">
          <a class="ayesearch-location-name" href="${escapeHtml(location.websiteUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(location.name)}</a>
          <div class="ayesearch-location-rating-row">
            <strong>${location.rating.toFixed(1)}</strong>
            ${renderStars(location.rating)}
            <a href="${escapeHtml(mapsSearchUrl(location.mapsQuery))}" target="_blank" rel="noopener noreferrer">(${location.reviewCount.toLocaleString()})</a>
            <span>·</span>
            <span>${escapeHtml(location.category)}</span>
          </div>
          <div class="ayesearch-location-status-row">
            <strong>${escapeHtml(location.statusText)}</strong>
            <span>·</span>
            <span>${escapeHtml(location.address)}</span>
          </div>
          ${location.description ? `<p class="ayesearch-location-description">${escapeHtml(location.description)}</p>` : ""}
        </div>
        <a class="ayesearch-location-image-link" href="${escapeHtml(location.websiteUrl)}" target="_blank" rel="noopener noreferrer" aria-label="Open ${escapeHtml(location.name)} website">
          ${image || `<span class="ayesearch-location-image-fallback">${escapeHtml(location.name.charAt(0))}</span>`}
        </a>
      </div>
      <div class="ayesearch-location-actions">
        <a href="tel:${escapeHtml(location.phone)}">Call</a>
        <a href="${escapeHtml(directionsUrl(location))}" target="_blank" rel="noopener noreferrer">Directions</a>
        <a href="${escapeHtml(location.websiteUrl)}" target="_blank" rel="noopener noreferrer">Website</a>
        <a href="${escapeHtml(mapsSearchUrl(location.mapsQuery))}" target="_blank" rel="noopener noreferrer">Reviews</a>
      </div>
    </article>`;
}

function bindLocationImages(container: HTMLElement): void {
  container.querySelectorAll<HTMLImageElement>(".ayesearch-location-image").forEach((image) => {
    image.addEventListener("error", () => {
      const link = image.closest<HTMLElement>(".ayesearch-location-image-link");
      if (!link) return;
      const article = image.closest<HTMLElement>(".ayesearch-location-item");
      const name = article?.querySelector<HTMLElement>(".ayesearch-location-name")?.textContent || "D";
      link.innerHTML = `<span class="ayesearch-location-image-fallback">${escapeHtml(name.charAt(0))}</span>`;
    });
  });
}

function bindShowMore(container: HTMLElement): void {
  const button = container.querySelector<HTMLButtonElement>("[data-locations-toggle]");
  if (!button) return;

  button.addEventListener("click", () => {
    const expanded = button.getAttribute("aria-expanded") === "true";
    container.querySelectorAll<HTMLElement>(".ayesearch-location-collapsed").forEach((item) => {
      item.classList.toggle("is-expanded", !expanded);
    });
    button.setAttribute("aria-expanded", String(!expanded));
    button.textContent = expanded ? "Show more" : "Show less";
  });
}

export function renderLocationsCard(container: HTMLElement, brandSlug: string): void {
  if (String(brandSlug || "").trim().toLowerCase() !== "lamborghini") {
    container.innerHTML = "";
    return;
  }

  const locations = LAMBORGHINI_SEATTLE_LOCATIONS;
  container.innerHTML = `
    <section class="ayesearch-locations-shell" aria-label="Luxury vehicle dealers near Seattle">
      <div class="ayesearch-locations-heading">
        <div>
          <div class="ayesearch-locations-kicker">PLACES</div>
          <h2>Luxury dealers near Seattle</h2>
        </div>
        <span>Seattle area</span>
      </div>

      <div class="ayesearch-locations-list">
        ${locations.map(renderLocation).join("")}
      </div>

      ${locations.length > 3 ? `<button class="ayesearch-locations-show-more" type="button" data-locations-toggle aria-expanded="false">Show more</button>` : ""}
    </section>`;

  bindLocationImages(container);
  bindShowMore(container);
}
